import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { App } from '@capacitor/app';
import { Location } from '@angular/common';
import { ApplicationFacade } from '@ridango/components';
import { AuthService, BaseComponent } from '@ridango/core';
import { DeviceIdService } from '@ridango/core/lib/services';
import Env from '@ridango/core/env';
import { takeUntil } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

@Component({
    templateUrl: './about-app.component.html'
})
export class AboutAppComponent extends BaseComponent implements OnInit, OnDestroy {
    appVersion: string;
    currentYear = new Date().getFullYear();
    assetsPath$ = this.applicationFacade.assetsPath$;
    customerId: string = null;
    isLoggedIn = false;
    anonymousId: string = null;

    constructor(
        @Inject('ENV') public env: Env,
        private location: Location,
        private applicationFacade: ApplicationFacade,
        private authService: AuthService,
        private deviceIdService: DeviceIdService
    ) {
        super();
    }

    async ngOnInit() {
        this.isLoggedIn = this.authService.getLoginStatus();

        if (this.isLoggedIn) {
            this.loadCustomerId();
        } else {
            this.loadAnonymousId();
        }

        try {
            const { version } = await App.getInfo();
            this.appVersion = 'v' + version;
        } catch (error) {
            console.error('Failed to get app version:', error);
        }
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    back() {
        this.location.back();
    }

    private loadAnonymousId(): void {
        if (this.isLoggedIn) {
            this.anonymousId = null;
            return;
        }

        this.deviceIdService
            .getDeviceCustomerId()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: (deviceCustomerId) => {
                    if (deviceCustomerId) {
                        this.anonymousId = String(deviceCustomerId);
                    } else {
                        this.loadFallbackDeviceId();
                    }
                },
                error: (error) => {
                    console.error('Failed to get device customer id:', error);
                    this.loadFallbackDeviceId();
                }
            });
    }

    private loadCustomerId(): void {
        const token = this.authService.getLocalAccessToken();

        if (!token) {
            this.customerId = null;
            return;
        }

        try {
            const decoded: any = jwtDecode(token);
            this.customerId = decoded?.afc_customer_account_id ? String(decoded.afc_customer_account_id) : null;
        } catch (error) {
            console.error('Failed to decode customer ID from token:', error);
            this.customerId = null;
        }
    }

    private loadFallbackDeviceId(): void {
        this.deviceIdService
            .getDeviceId()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: (id) => (this.anonymousId = id),
                error: (error) => console.error('Failed to get device ID:', error)
            });
    }
}
